/**
 * L'écran de recherche d'un sous-traitant — qui appeler pour cet axe.
 *
 * Cadrage complet dans « Stafa v2 — Étape 7 : la recherche d'un sous-traitant ».
 * Le moteur est `apps/api/src/affretement.ts`, et il est COMMUN aux deux portes
 * que le cadrage veut : cet écran, où l'on saisit l'axe, et le panneau ouvert
 * depuis le champ Véhicule d'un ordre, où l'axe se déduit. Deux moteurs
 * auraient fini par classer différemment.
 *
 * Même découpe que partout : `VueAffretement` dessine, `EcranAffretement`
 * décide, et le calcul est dans `affretement.ts`, en fonctions pures.
 *
 *
 * CET ÉCRAN N'ÉCRIT RIEN
 * ---------------------------------------------------------------------------
 * Il cherche, il présente, il coche. La cotation groupée — l'envoi en Cci aux
 * transporteurs retenus — est du second lot : elle demande un service d'envoi,
 * un domaine authentifié (SPF, DKIM) et la trace de ce qui est parti. Le
 * bouton existe et le dit, plutôt que de disparaître : c'est le geste qui
 * termine la recherche, et le cacher ferait croire qu'on a oublié.
 *
 *
 * DEUX CHOIX QUI SE VOIENT
 * ---------------------------------------------------------------------------
 *
 *   - **les zones se choisissent dans une liste, pas en texte libre.** Taper
 *     « FR45 » à la main marcherait, mais taper « 45 » ne marcherait pas, et
 *     rien ne le dirait. La liste vient du référentiel, raccourcis en tête ;
 *   - **le décompte est la moitié de l'écran.** Quand il ne trouve personne,
 *     il dit LEQUEL des deux filtres a tout écarté. « Un écran vide qui
 *     n'explique pas est un écran raté. »
 */

import { useCallback, useEffect, useState } from 'react';
import {
  chercherTransporteurs,
  lireOrdres,
  lireVehicules,
  lireZones,
  type ExplicationVide,
  type TransporteurTrouve,
  type Vehicule,
  type Zone,
} from './api.js';
import {
  axeDit,
  decompteACouvrir,
  decompteAffretes,
  decompteRecherche,
  lieuDit,
  lignesAffretees,
  margeDite,
  ordresACouvrir,
  type LigneAffretee,
  type OrdreACouvrir,
} from './affretement.js';
import { enEuros } from './ot.js';

// ---------------------------------------------------------------------------
// Les états
// ---------------------------------------------------------------------------

type EtatResultat =
  | { readonly nom: 'vierge' }
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly transporteurs: readonly TransporteurTrouve[];
      readonly explication: ExplicationVide | null;
    };

export interface Critere {
  readonly depart: string;
  readonly arrivee: string;
  readonly vehicule: string;
}

/** Les deux onglets. « Affrétés » d'abord : c'est l'état des lieux. */
export type OngletAffretement = 'affretes' | 'recherche';

export type EtatAffretes =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | { readonly nom: 'prete'; readonly lignes: readonly LigneAffretee[] };

export interface ProprietesVueAffretement {
  readonly onglet: OngletAffretement;
  readonly quandOnglet: (onglet: OngletAffretement) => void;
  /** L'onglet Affrétés : sa fenêtre de dates, son état, son geste. */
  readonly fenetre: { readonly du: string; readonly au: string };
  readonly quandFenetre: (bornes: { readonly du: string; readonly au: string }) => void;
  readonly affretes: EtatAffretes;
  readonly quandOuvrirOrdre: (otId: string) => void;
  readonly critere: Critere;
  readonly zones: readonly Zone[];
  readonly materiels: readonly Vehicule[];
  readonly resultat: EtatResultat;
  readonly cochees: ReadonlySet<string>;
  readonly quandCritere: (cle: keyof Critere, valeur: string) => void;
  readonly quandChercher: () => void;
  readonly quandCocher: (id: string, coche: boolean) => void;
  /**
   * LE SÉLECTEUR D'ORDRE, demandé le 09/09/2026 — « pour quel OT cherche-t-on
   * une solution ». Il vient de la MÊME lecture que l'onglet Affrétés : une
   * seule liste d'ordres, deux usages.
   */
  readonly aCouvrir: readonly OrdreACouvrir[];
  readonly ordreChoisi: string;
  readonly quandOrdre: (id: string) => void;
}

function Absent() {
  return <span className="absent">—</span>;
}

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export function VueAffretement(p: ProprietesVueAffretement) {
  const axe = axeDit({
    depart: p.critere.depart === '' ? null : p.critere.depart,
    arrivee: p.critere.arrivee === '' ? null : p.critere.arrivee,
    vehicule: p.critere.vehicule === '' ? null : p.critere.vehicule,
  });

  /** L'ordre choisi, quand il ne donne pas son axe. `undefined` sinon. */
  const ordreSansAxe = p.aCouvrir.find((o) => o.id === p.ordreChoisi && !o.axeConnu);

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {/* DEUX ONGLETS, « Affrétés » en premier. Le module Affrètement avait
            trois pages dans la maquette du 05/09 ; il n'en restait que la
            recherche, et rien ne disait ce qui était déjà confié — constaté à
            l'essai le 07/09/2026. « À affréter » n'est pas repris : c'est la
            liste des ordres, filtrée, et une seconde porte sur la même chose
            se serait mise à diverger. */}
        <div className="onglets-listes">
          <button
            type="button"
            className={p.onglet === 'affretes' ? 'onglet-liste actif' : 'onglet-liste'}
            onClick={() => {
              p.quandOnglet('affretes');
            }}
          >
            Affrétés
          </button>
          <button
            type="button"
            className={p.onglet === 'recherche' ? 'onglet-liste actif' : 'onglet-liste'}
            onClick={() => {
              p.quandOnglet('recherche');
            }}
          >
            Chercher un transporteur
          </button>
        </div>

        {p.onglet === 'affretes' && <Affretes p={p} />}

        {p.onglet === 'recherche' && (
          <>
            <form
              className="barre-outils"
              onSubmit={(e) => {
                e.preventDefault();
                p.quandChercher();
              }}
            >
              {/* L'ORDRE D'ABORD, puis ce qu'il pose. Demandé le 09/09/2026, et
              c'est le retour au cadrage §2 : « l'axe et le matériel ne se
              saisissent pas, ils se déduisent de l'ordre ».

              IL PRÉ-REMPLIT, IL NE VERROUILLE PAS. Les trois listes restent
              modifiables — tout l'écran est fait pour qu'on RELÂCHE un filtre
              quand personne ne sort, et un axe verrouillé tuerait ce
              mécanisme. « Aucun » rend la recherche libre, celle qu'on fait
              avant d'avoir le transport. */}
              <label className="champ-date champ-ordre">
                <span className="etiquette">Pour l’ordre</span>
                <select
                  className="champ"
                  value={p.ordreChoisi}
                  onChange={(e) => {
                    p.quandOrdre(e.target.value);
                  }}
                >
                  <option value="">Aucun — recherche libre</option>
                  {p.aCouvrir.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.texte}
                    </option>
                  ))}
                </select>
              </label>

              {/* LES ZONES VIENNENT DU RÉFÉRENTIEL, raccourcis nationaux en tête :
              c'est l'ordre que la migration a posé, et c'est « France
              entière » qu'on coche le plus souvent. */}
              <label className="champ-date">
                <span className="etiquette">Départ</span>
                <select
                  className="champ"
                  value={p.critere.depart}
                  onChange={(e) => {
                    p.quandCritere('depart', e.target.value);
                  }}
                >
                  <option value="">Partout</option>
                  {p.zones.map((z) => (
                    <option key={z.code} value={z.code}>
                      {z.code} · {z.nom}
                    </option>
                  ))}
                </select>
              </label>

              <label className="champ-date">
                <span className="etiquette">Arrivée</span>
                <select
                  className="champ"
                  value={p.critere.arrivee}
                  onChange={(e) => {
                    p.quandCritere('arrivee', e.target.value);
                  }}
                >
                  <option value="">Partout</option>
                  {p.zones.map((z) => (
                    <option key={z.code} value={z.code}>
                      {z.code} · {z.nom}
                    </option>
                  ))}
                </select>
              </label>

              <label className="champ-date">
                <span className="etiquette">Matériel</span>
                <select
                  className="champ"
                  value={p.critere.vehicule}
                  onChange={(e) => {
                    p.quandCritere('vehicule', e.target.value);
                  }}
                >
                  <option value="">Tous</option>
                  {/* LE RÉFÉRENTIEL DES 24 CODES, et non la liste réglée des
                  types : le filtre de la recherche porte sur
                  `transporteur_vehicules.vehicule_code`, qui est un code de
                  carrosserie + gamme — « T6 », « TD6 ». Ce sont les mêmes que
                  la feuille Google de TNS, à la lettre. */}
                  {p.materiels.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.code} · {m.libelle}
                    </option>
                  ))}
                </select>
              </label>

              <button className="bouton" type="submit">
                Chercher
              </button>
            </form>

            {/* L'AXE MANQUANT SE DIT, il ne se devine pas. Une étape sans pays ne
            donne pas de zone — les ordres saisis avant le 06/09/2026 n'en ont
            pas. Sans cette phrase, l'exploitant verrait « Partout » et croirait
            que Stafa n'a rien trouvé, alors qu'il n'a rien pu déduire. */}
            {ordreSansAxe !== undefined && (
              <p className="aide-saisie">
                {ordreSansAxe.texte} — l’axe ne se déduit pas de cet ordre : une étape
                n’a pas de pays. Choisissez les zones à la main.
              </p>
            )}

            {p.resultat.nom === 'vierge' && (
              <div className="vide">
                <p>
                  Choisissez un ordre, ou saisissez un axe et un matériel — puis
                  cherchez. Sans aucun critère, la liste rend tous les transporteurs
                  actifs.
                </p>
                <p className="secondaire">{decompteACouvrir(p.aCouvrir.length)}</p>
              </div>
            )}

            {p.resultat.nom === 'chargement' && <p className="attente">Recherche…</p>}

            {p.resultat.nom === 'panne' && (
              <p className="bandeau-refus">La recherche n’a pas abouti.</p>
            )}

            {p.resultat.nom === 'prete' && (
              <>
                {/* LA PHRASE QUI FAIT L'ÉCRAN. Quand elle dit « aucun », elle dit
                aussi lequel des deux filtres a tout écarté — sinon on ne sait
                pas quoi relâcher. */}
                <p className="decompte">
                  {axe !== null && <span className="code">{axe}</span>}
                  {axe !== null && ' — '}
                  {decompteRecherche(
                    p.resultat.transporteurs.length,
                    p.resultat.explication,
                  )}
                </p>

                {p.resultat.transporteurs.length > 0 && (
                  <div className="tableau-cadre">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col" />
                          <th scope="col" className="col-souple">
                            Transporteur
                          </th>
                          <th scope="col" className="col-large">
                            Où
                          </th>
                          <th scope="col">Téléphone</th>
                          <th scope="col" className="col-large">
                            Courriel
                          </th>
                          <th scope="col" className="col-large">
                            Matériel
                          </th>
                          <th scope="col">Zones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.resultat.transporteurs.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Retenir ${t.nom}`}
                                checked={p.cochees.has(t.id)}
                                onChange={(e) => {
                                  p.quandCocher(t.id, e.target.checked);
                                }}
                              />
                            </td>
                            <td className="col-souple">
                              {t.nom}
                              {t.code !== null && (
                                <span className="secondaire">{t.code}</span>
                              )}
                            </td>
                            <td className="col-large">{lieuDit(t) ?? <Absent />}</td>
                            {/* LE TÉLÉPHONE EST LA COLONNE UTILE : on cherche pour
                            APPELER. Le contact de la section exploitation vit
                            sur la fiche — `contacts.fonction` est du texte
                            libre, et on ne peut pas le trancher ici. */}
                            <td className="code">{t.telephone ?? <Absent />}</td>
                            <td className="col-large">{t.courriel ?? <Absent />}</td>
                            <td className="code col-large">
                              {t.vehicules === null || t.vehicules.length === 0 ? (
                                <Absent />
                              ) : (
                                t.vehicules.join(' · ')
                              )}
                            </td>
                            <td className="code">{t.zones}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {p.cochees.size > 0 && (
                  <p className="aide-saisie">
                    {/* La phrase sur la cotation groupée « pas encore construite » est
                        retirée — refonte graphique, maquettes du 31/10/2026. */}
                    {p.cochees.size > 1
                      ? `${String(p.cochees.size)} transporteurs retenus`
                      : '1 transporteur retenu'}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'onglet « Affrétés »
// ---------------------------------------------------------------------------

/**
 * CE QUI EST DÉJÀ CONFIÉ, et à qui, et pour combien.
 *
 * La marge est en couleur, comme dans le panneau d'affrètement — mêmes trois
 * crans, même fonction. Le chiffre porte le sens ; la couleur le répète.
 *
 * On clique une ligne pour ouvrir l'ordre : c'est là qu'on annule un
 * affrètement, pas ici. Cet onglet REGARDE, il n'écrit pas.
 */
function Affretes({ p }: { readonly p: ProprietesVueAffretement }) {
  const lignes = p.affretes.nom === 'prete' ? p.affretes.lignes : [];

  return (
    <>
      <div className="barre-outils">
        <label className="champ-date">
          <span className="etiquette">Du</span>
          <input
            className="champ"
            type="date"
            value={p.fenetre.du}
            onChange={(e) => {
              p.quandFenetre({ du: e.target.value, au: p.fenetre.au });
            }}
          />
        </label>
        <label className="champ-date">
          <span className="etiquette">au</span>
          <input
            className="champ"
            type="date"
            value={p.fenetre.au}
            onChange={(e) => {
              p.quandFenetre({ du: p.fenetre.du, au: e.target.value });
            }}
          />
        </label>
      </div>

      {p.affretes.nom === 'chargement' && <p className="attente">Chargement…</p>}
      {p.affretes.nom === 'panne' && (
        <p className="bandeau-refus">La liste des affrètements n’a pas abouti.</p>
      )}

      {p.affretes.nom === 'prete' && (
        <>
          <p className="decompte">{decompteAffretes(lignes.length)}</p>

          {lignes.length > 0 && (
            <div className="tableau-cadre">
              <table>
                <thead>
                  <tr>
                    <th scope="col">N° d’OT</th>
                    <th scope="col" className="col-souple">
                      Client
                    </th>
                    <th scope="col">Axe</th>
                    <th scope="col" className="col-souple">
                      Transporteur
                    </th>
                    <th scope="col" className="prix">
                      Achat HT
                    </th>
                    <th scope="col" className="prix">
                      Vente HT
                    </th>
                    <th scope="col" className="prix">
                      Marge
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr
                      className="rangee"
                      key={l.id}
                      tabIndex={0}
                      onClick={() => {
                        p.quandOuvrirOrdre(l.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          p.quandOuvrirOrdre(l.id);
                        }
                      }}
                    >
                      <td className="code">{l.numero}</td>
                      <td className="col-souple">{l.client}</td>
                      <td className="code">{l.axe ?? <Absent />}</td>
                      <td className="col-souple">{l.transporteur ?? <Absent />}</td>
                      <td className="prix">{enEuros(l.achat) ?? <Absent />}</td>
                      <td className="prix">{enEuros(l.vente) ?? <Absent />}</td>
                      <td
                        className={
                          l.marge === null ? 'prix' : `prix marge-${l.marge.couleur}`
                        }
                      >
                        {l.marge === null ? <Absent /> : margeDite(l.marge)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

export interface ProprietesAffretement {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** Ouvrir un ordre depuis l'onglet Affrétés — même geste que le planning. */
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function EcranAffretement({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesAffretement) {
  const [onglet, setOnglet] = useState<OngletAffretement>('affretes');
  // AUCUN FILTRE À L'ARRIVÉE — même règle que la liste des ordres, demandée le
  // 07/09/2026. On voit TOUS les affrètements ; on borne ensuite si on veut.
  const [fenetre, setFenetre] = useState({ du: '', au: '' });
  const [affretes, setAffretes] = useState<EtatAffretes>({ nom: 'chargement' });
  const [critere, setCritere] = useState<Critere>({
    depart: '',
    arrivee: '',
    vehicule: '',
  });
  const [zones, setZones] = useState<readonly Zone[]>([]);
  const [materiels, setMateriels] = useState<readonly Vehicule[]>([]);
  const [resultat, setResultat] = useState<EtatResultat>({ nom: 'vierge' });
  const [cochees, setCochees] = useState<ReadonlySet<string>>(new Set());
  // LE SÉLECTEUR D'ORDRE. La liste vient de la MÊME lecture que les affrétés —
  // un seul `GET /ordres-transport` pour les deux onglets. Deux appels sur la
  // même fenêtre auraient décrit deux instants.
  const [aCouvrir, setACouvrir] = useState<readonly OrdreACouvrir[]>([]);
  const [ordreChoisi, setOrdreChoisi] = useState('');

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Les deux référentiels, chargés UNE FOIS.
   *
   * Ils ne changent pas pendant qu'on cherche, et les relire à chaque
   * recherche ferait deux appels de plus pour un contenu identique.
   */
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const [lues, materiel] = await Promise.all([lireZones(j), lireVehicules(j)]);
      if (abandonne) return;
      if (lues.etat === 'ok') setZones(lues.valeur);
      if (materiel.etat === 'ok') setMateriels(materiel.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  /**
   * LES AFFRÉTÉS, relus à chaque changement de fenêtre.
   *
   * `GET /ordres-transport` et rien d'autre : les trois colonnes de
   * l'affrètement y sont déjà. Le filtre se fait ici, sur `affretement_id`,
   * parce que l'API n'a pas de paramètre pour ça et que lui en ajouter un
   * serait une route de plus pour la même liste.
   */
  useEffect(() => {
    let abandonne = false;
    setAffretes({ nom: 'chargement' });
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lus = await lireOrdres(j, { du: fenetre.du, au: fenetre.au });
      if (abandonne) return;
      if (lus.etat === 'ok') {
        setAffretes({ nom: 'prete', lignes: lignesAffretees([...lus.valeur.ordres]) });
        // LES DEUX DÉRIVATIONS DE LA MÊME RÉPONSE : ce qui est déjà confié, et
        // ce qui reste à couvrir. Complémentaires par construction — l'une
        // garde `affretement_id` non nul, l'autre nul.
        setACouvrir(ordresACouvrir([...lus.valeur.ordres]));
      } else {
        if (lus.cause === 'acces') quandAccesPerdu();
        setAffretes({ nom: 'panne' });
        setACouvrir([]);
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton, fenetre.du, fenetre.au, quandAccesPerdu]);

  /**
   * LE CRITÈRE PASSE EN ARGUMENT, il n'est pas lu dans l'état.
   *
   * Choisir un ordre pose les trois filtres ET lance la recherche, en un seul
   * geste. Si cette fonction lisait `critere`, elle lirait la valeur d'AVANT
   * le choix — `setCritere` ne prend effet qu'au rendu suivant. Le bug aurait
   * été silencieux : une recherche sur l'axe précédent, plausible à l'écran.
   */
  const chercher = useCallback(
    async (avec: Critere) => {
      setResultat({ nom: 'chargement' });
      // LES COCHES REPARTENT À CHAQUE RECHERCHE : garder une case cochée sur un
      // transporteur qui n'est plus dans la liste enverrait une cotation à
      // quelqu'un qu'on ne voit plus à l'écran.
      setCochees(new Set());

      const j = await avecJeton();
      if (j === null) return;
      const trouve = await chercherTransporteurs(j, {
        depart: avec.depart === '' ? null : avec.depart,
        arrivee: avec.arrivee === '' ? null : avec.arrivee,
        vehicule: avec.vehicule === '' ? null : avec.vehicule,
      });
      if (trouve.etat === 'ok') {
        setResultat({ nom: 'prete', ...trouve.valeur });
      } else {
        if (trouve.cause === 'acces') quandAccesPerdu();
        setResultat({ nom: 'panne' });
      }
    },
    [avecJeton, quandAccesPerdu],
  );

  return (
    <VueAffretement
      onglet={onglet}
      quandOnglet={setOnglet}
      fenetre={fenetre}
      quandFenetre={setFenetre}
      affretes={affretes}
      quandOuvrirOrdre={quandOuvrirOrdre}
      critere={critere}
      zones={zones}
      materiels={materiels}
      resultat={resultat}
      cochees={cochees}
      quandCritere={(cle, valeur) => {
        setCritere((a) => ({ ...a, [cle]: valeur }));
      }}
      quandChercher={() => {
        void chercher(critere);
      }}
      aCouvrir={aCouvrir}
      ordreChoisi={ordreChoisi}
      quandOrdre={(id) => {
        setOrdreChoisi(id);
        // « Aucun » remet la recherche libre : on efface les trois filtres
        // plutôt que de laisser en place l'axe d'un ordre qu'on vient de
        // désélectionner — l'écran mentirait sur ce qu'il cherche.
        const pose: Critere =
          id === ''
            ? { depart: '', arrivee: '', vehicule: '' }
            : (() => {
                const o = aCouvrir.find((c) => c.id === id);
                return o === undefined
                  ? { depart: '', arrivee: '', vehicule: '' }
                  : { depart: o.depart, arrivee: o.arrivee, vehicule: o.vehicule };
              })();
        setCritere(pose);
        // UN SEUL GESTE : choisir l'ordre pose les filtres ET cherche. Le
        // critère part en argument — voir `chercher`, l'état n'est pas encore
        // à jour ici.
        void chercher(pose);
      }}
      quandCocher={(id, coche) => {
        setCochees((a) => {
          const suivant = new Set(a);
          if (coche) suivant.add(id);
          else suivant.delete(id);
          return suivant;
        });
      }}
    />
  );
}
